// src/pages/admin/AdminCoupons.js
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { ordersApi, unwrapPage, getErrorMessage, getFieldErrors } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Button, Input, Select, Modal, ConfirmDialog, Badge } from '../../components/ui';
import { ErrorBox, TableRowSkeleton } from '../../components/common/ErrorBoundary';

// ── Coupon Form Modal ──────────────────────────────────────────────────────────
function CouponModal({ coupon, onSave, onClose }) {
  const { toast } = useToast();
  const isEdit    = Boolean(coupon);
  const [form, setForm] = useState({
    code:            coupon?.code            || '',
    discount_type:   coupon?.discount_type   || 'percent',
    discount_value:  coupon?.discount_value  || '',
    min_order_value: coupon?.min_order_value || '0',
    max_uses:        coupon?.max_uses        || '100',
    is_active:       coupon?.is_active       ?? true,
  });
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);

  const set = f => e => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(p => ({ ...p, [f]: v }));
    setErrors(p => ({ ...p, [f]: '' }));
  };

  const save = async () => {
    const errs = {};
    if (!form.code.trim() || form.code.trim().length < 3) errs.code = 'Code must be 3–20 chars.';
    if (!form.discount_value || parseFloat(form.discount_value) <= 0) errs.discount_value = 'Enter a valid value.';
    if (form.discount_type === 'percent' && parseFloat(form.discount_value) > 100)
      errs.discount_value = 'Cannot exceed 100%.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const payload = { ...form, code: form.code.trim().toUpperCase() };
      if (isEdit) {
        await ordersApi.adminUpdateCoupon(coupon.id, payload);
        toast.success(`Coupon "${payload.code}" updated.`);
      } else {
        await ordersApi.adminCreateCoupon(payload);
        toast.success(`Coupon "${payload.code}" created.`);
      }
      onSave();
    } catch (err) {
      const fieldErrs = getFieldErrors(err);
      if (Object.keys(fieldErrs).length) setErrors(fieldErrs);
      else toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open title={isEdit ? 'Edit Coupon' : 'Create Coupon'} onClose={onClose} maxWidth="max-w-md">
      <div className="space-y-4">
        <Input label="Coupon Code" value={form.code} onChange={set('code')}
          error={errors.code} required placeholder="e.g. DIWALI10" className="uppercase"/>
        <Select label="Discount Type"
          options={[{value:'percent',label:'Percentage (%)'},{value:'fixed',label:'Fixed Amount (₹)'}]}
          value={form.discount_type} onChange={set('discount_type')}/>
        <Input label={`Discount Value (${form.discount_type==='percent'?'%':'₹'})`}
          type="number" value={form.discount_value} onChange={set('discount_value')}
          error={errors.discount_value} required min="1"
          max={form.discount_type==='percent'?'100':undefined}/>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Min Order (₹)" type="number" value={form.min_order_value}
            onChange={set('min_order_value')} min="0"/>
          <Input label="Max Uses" type="number" value={form.max_uses}
            onChange={set('max_uses')} min="1"/>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={set('is_active')}
            className="w-4 h-4 accent-red-600 rounded"/>
          <span className="text-sm font-semibold text-gray-700">Active</span>
        </label>
      </div>
      <div className="flex gap-3 mt-5 pt-5 border-t border-gray-100">
        <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button className="flex-1" onClick={save} loading={loading}>
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AdminCoupons() {
  const { toast }              = useToast();
  const [coupons,   setCoupons]  = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [error,     setError]    = useState(null);
  const [modal,     setModal]    = useState(null);
  const [delTarget, setDel]      = useState(null);
  const [delLoad,   setDelLoad]  = useState(false);
  const [toggling,  setTog]      = useState(null);
  const [checkedIds,setChecked]  = useState(new Set());
  const [bulkLoad,  setBulkLoad] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    ordersApi.adminCoupons()
      .then(res => { setCoupons(unwrapPage(res).data); setError(null); })
      .catch(err => { setError(err); setCoupons([]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Single delete
  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await ordersApi.adminDeleteCoupon(delTarget.id);
      toast.success(`Coupon "${delTarget.code}" deleted.`);
      setDel(null); load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDelLoad(false);
    }
  };

  // Toggle active status
  const toggleActive = async c => {
    setTog(c.id);
    try {
      await ordersApi.adminUpdateCoupon(c.id, { is_active: !c.is_active });
      toast.success(`"${c.code}" ${!c.is_active ? 'enabled' : 'disabled'}.`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTog(null);
    }
  };

  // Bulk selection
  const toggleSelect = id => setChecked(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const toggleAll = () => setChecked(
    checkedIds.size === coupons.length ? new Set() : new Set(coupons.map(c => c.id))
  );

  // Bulk delete
  const handleBulkDelete = async () => {
    if (!checkedIds.size) return;
    if (!window.confirm(`DELETE ${checkedIds.size} coupon(s)? This cannot be undone.`)) return;
    setBulkLoad(true);
    try {
      await Promise.all([...checkedIds].map(id => ordersApi.adminDeleteCoupon(id)));
      toast.success(`${checkedIds.size} coupon(s) deleted.`);
      setChecked(new Set()); load();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setBulkLoad(false); }
  };

  // Bulk activate / deactivate
  const handleBulkToggle = async (activate) => {
    if (!checkedIds.size) return;
    if (!window.confirm(`${activate ? 'Activate' : 'Deactivate'} ${checkedIds.size} coupon(s)?`)) return;
    setBulkLoad(true);
    try {
      const toUpdate = coupons.filter(c => checkedIds.has(c.id));
      await Promise.all(toUpdate.map(c =>
        ordersApi.adminUpdateCoupon(c.id, { ...c, is_active: activate })
      ));
      toast.success(`${checkedIds.size} coupon(s) ${activate ? 'activated' : 'deactivated'}.`);
      setChecked(new Set()); load();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setBulkLoad(false); }
  };

  return (
    <AdminLayout title="🎟️ Coupon Codes">
      {/* Toolbar */}
      <div className="flex justify-end mb-5">
        <Button onClick={() => setModal('new')} icon="+" size="md">Create Coupon</Button>
      </div>

      {/* Bulk action bar */}
      {checkedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-blue-50 border border-blue-200
          rounded-2xl px-4 py-3 animate-fade-in">
          <span className="text-blue-700 font-black text-sm">{checkedIds.size} selected</span>
          <div className="flex flex-wrap gap-2 ml-2">
            <button onClick={() => handleBulkToggle(true)} disabled={bulkLoad}
              className="bg-green-100 text-green-700 hover:bg-green-200 font-bold text-xs
                px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50">
              ✅ Activate
            </button>
            <button onClick={() => handleBulkToggle(false)} disabled={bulkLoad}
              className="bg-amber-100 text-amber-700 hover:bg-amber-200 font-bold text-xs
                px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50">
              ⏸ Deactivate
            </button>
            <button onClick={handleBulkDelete} disabled={bulkLoad}
              className="bg-red-100 text-red-700 hover:bg-red-200 font-bold text-xs
                px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50">
              🗑 Delete
            </button>
          </div>
          <button onClick={() => setChecked(new Set())}
            className="ml-auto text-gray-400 hover:text-gray-600 text-xs font-bold">
            ✕ Clear
          </button>
        </div>
      )}

      {error && !loading && <ErrorBox error={error} onRetry={load} className="mb-4"/>}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-400 text-[11px] uppercase tracking-widest">
                <th className="px-4 py-3">
                  <input type="checkbox"
                    checked={coupons.length > 0 && checkedIds.size === coupons.length}
                    onChange={toggleAll}
                    className="w-4 h-4 accent-red-600 cursor-pointer"/>
                </th>
                <th className="px-5 py-3 text-left">Code</th>
                <th className="px-5 py-3 text-left">Discount</th>
                <th className="px-5 py-3 text-left">Min Order</th>
                <th className="px-5 py-3 text-left">Usage</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(4)].map((_,i) => <TableRowSkeleton key={i} cols={7}/>)
              ) : coupons.length === 0 && !error ? (
                <tr><td colSpan={7} className="py-12 text-center text-gray-400 text-sm">
                  No coupons yet
                </td></tr>
              ) : coupons.map(c => {
                const exhausted = c.used_count >= c.max_uses;
                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <input type="checkbox"
                        checked={checkedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        className="w-4 h-4 accent-red-600 cursor-pointer"/>
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-black bg-amber-100 text-amber-800
                        px-3 py-1 rounded-lg tracking-widest text-sm font-mono">
                        {c.code}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-bold text-gray-700">
                      {c.discount_type==='percent' ? `${c.discount_value}% OFF` : `₹${c.discount_value} OFF`}
                    </td>
                    <td className="px-5 py-4 text-gray-500">₹{c.min_order_value}</td>
                    <td className="px-5 py-4">
                      <span className={`font-black ${exhausted?'text-red-600':'text-gray-800'}`}>
                        {c.used_count}
                      </span>
                      <span className="text-gray-400"> / {c.max_uses}</span>
                      {exhausted && <Badge color="red" className="ml-2">Exhausted</Badge>}
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => toggleActive(c)} disabled={toggling === c.id}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition-colors
                          ${c.is_active
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {toggling === c.id ? '…' : c.is_active ? '● Active' : '○ Disabled'}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-3">
                        <button onClick={() => setModal(c)}
                          className="text-blue-600 hover:text-blue-800 font-bold text-xs">Edit</button>
                        <button onClick={() => setDel(c)}
                          className="text-red-400 hover:text-red-600 font-bold text-xs">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <CouponModal
          coupon={modal === 'new' ? null : modal}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
      <ConfirmDialog
        open={Boolean(delTarget)}
        onClose={() => setDel(null)}
        onConfirm={handleDelete}
        loading={delLoad}
        title="Delete Coupon?"
        message={`"${delTarget?.code}" will be permanently deleted. This cannot be undone.`}
        confirmLabel="Delete"
      />
    </AdminLayout>
  );
}
